import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlTree } from '@angular/router';
import { PermissionEnum } from '@core/enums/permission.enum';
import { ErrorCodeEnum } from '@core/enums/error-code.enum';
import { IMenuConfig } from '@core/models/interfaces/menu-config.interface';
import { MenuService } from '@core/services/menu.service';
import { CoreStore } from '@core/store/core.store';
import { accessEvents } from '@core/store/core.events';
import { injectDispatch } from '@ngrx/signals/events';
import { userRetrieved$ } from '@core/store/core-store.utils';
import { map, Observable } from 'rxjs';

const findMenuItem = (items: IMenuConfig[], id: string): IMenuConfig | undefined => {
  return (items || []).find(({ id: menuId }) => id === menuId);
};

export const hasPermissionsGuard: CanMatchFn = (route, segments): Observable<boolean | UrlTree> => {
  const coreStore = inject(CoreStore);
  const accessDispatch = injectDispatch(accessEvents);
  const router: Router = inject(Router);
  const menuService: MenuService = inject(MenuService);

  const menuId: string = route.data?.['menuId'] ?? '';
  const menuEnabled: boolean = menuId ? findMenuItem(menuService.config, menuId)?.enable !== false : true;
  const permissions: PermissionEnum[] = route.data?.['permissions'] ?? [];
  const atLeastPermissions: PermissionEnum[] = route.data?.['atLeastPermissions'] ?? [];

  return userRetrieved$().pipe(
    map(() => {
      const userPermissions: PermissionEnum[] = coreStore.userPermissions();

      let hasAuthorization: boolean;
      if(!!permissions?.length) {
        const hasAllPermissions: boolean = permissions.every(permission => userPermissions.includes(permission));
        hasAuthorization = !!atLeastPermissions?.length
          ? hasAllPermissions && userPermissions.some(userPermission => atLeastPermissions.includes(userPermission))
          : hasAllPermissions;
      } else if(!!atLeastPermissions?.length) {
        hasAuthorization = userPermissions.some(userPermission => atLeastPermissions.includes(userPermission));
      } else hasAuthorization = true;

      const isAuthorized: boolean = hasAuthorization && menuEnabled;

      if(!isAuthorized) {
        accessDispatch.denied(ErrorCodeEnum.FORBIDDEN);
        return router.createUrlTree(['/error-page']);
      }

      return true;
    })
  );
};
